import {useCallback, useContext, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectTextAreaOpen} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import './TextEditorLarge.sass';

export const TextEditorLarge = () => {
  const {canvas} = useContext(EventCreateContext);
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const textAreaOpen = useSelector(selectTextAreaOpen);
  const dispatch = useDispatch();
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const activeObject = canvas?.getActiveObject();
    setText(activeObject?.text);
    setOriginalText(activeObject?.text);
  }, [canvas, textAreaOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const onTextEditingEntered = () =>
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: true},
      });

    canvas?.on({
      'text:editing:entered': onTextEditingEntered,
    });

    return () => {
      canvas?.off({
        'text:editing:entered': onTextEditingEntered,
      });
    };
  }, [canvas]);

  const changeText = useCallback(
    (newText) => {
      const activeObject = canvas?.getActiveObject();
      if (activeObject) {
        setText(newText);
        activeObject.text = newText;
      }
    },
    [canvas?.getActiveObject]
  );

  const onTextChangeHandler = useCallback(
    ({target: {value}}) => changeText(value),

    [changeText]
  );

  const closeEditor = () => {
    const activeObject = canvas?.getActiveObject();
    activeObject?.exitEditing?.();
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {textAreaOpen: false},
    });
    canvas.requestRenderAll();
  };

  const onClearClickHandler = () => changeText('');

  const onCancelClickHandler = () => {
    changeText(originalText);
    closeEditor();
  };

  const onDoneClickHandler = () => {
    changeText(text); // prevent automatic tests non save
    closeEditor();
  };

  const handleFocus = (event) => event.target.select();

  if (isDesktop || !textAreaOpen) {
    return null;
  }

  return createPortal(
    <div className={`text-editor-mw-large ${textAreaOpen ? 'text-editor-mw-large--visible' : ''}`}>
      <div className="text-editor-mw-large__header">
        <Button
          data-qa-id="step1-texteditor-cancel"
          className="text-editor-mw-large__header__cancel_btn"
          variant="link"
          size="small"
          onClick={onCancelClickHandler}
        >
          Cancel
        </Button>
        <Button
          data-qa-id="step1-texteditor-clear"
          className="text-editor-mw-large__header__clear_btn"
          variant="link"
          size="small"
          onClick={onClearClickHandler}
        >
          Clear
        </Button>
        <Button
          data-qa-id="step1-texteditor-done"
          className="text-editor-mw-large__header__done_btn"
          variant="link"
          size="small"
          onClick={onDoneClickHandler}
        >
          Done
        </Button>
      </div>
      <div className="text-editor-mw-large__body">
        <textarea
          onFocus={handleFocus}
          onChange={onTextChangeHandler}
          value={text}
          // biome-ignore lint/a11y/noAutofocus: remove autofocus?
          autoFocus
          key={originalText}
          data-qa-id="step1-texteditor-input"
        />
      </div>
    </div>,
    document.getElementById('pbModalRoot')
  );
};
